import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { memberRepositoryToken } from './repository';
import type { MemberDetail } from './types';

export function ReferralCodeDialog({
  member,
  onClose,
  onSaved,
  open,
}: {
  readonly member: MemberDetail;
  readonly onClose: () => void;
  readonly onSaved: (member: MemberDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(memberRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<{ reason: string }>({ defaultValues: { reason: '' } });
  useEffect(() => {
    if (!open) return;
    reset({ reason: '' });
    setError(null);
    guard.current = false;
    setBusy(false);
  }, [open, reset]);
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async ({ reason }) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.rotateReferralCode(member.id, { reason: reason.trim() });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('members.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('members.referral.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="member-referral-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            <Typography>
              {t('members.referral.impact', {
                code: member.activeReferralCodes[0] ?? t('common.notAvailable'),
              })}
            </Typography>
            <Typography variant="body2">{t('members.referral.relationships')}</Typography>
          </Alert>
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('members.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('members.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          form="member-referral-form"
          loading={busy}
          type="submit"
          variant="contained"
        >
          {t('members.actions.rotateReferral')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
